import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, IconButton, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { getLotUserContent } from '../../../../services/lotUserContentService';
import LotUserContentForm from './LotUserContentForm';
import LotUserContentView from './LotUserContentView';
import LotUserContentSharedSection from './LotUserContentSharedSection';
import {
  LotInfoCollapseSection,
  LOT_INFO_DETAIL_SURFACE_SX,
  lotInfoRowOuterSx,
  LOT_INFO_ROW_INNER_SX,
  lotInfoFieldLabelLayoutSx,
  lotInfoFieldValueSx,
  LOT_INFO_FIELD_ADORNMENT_SX,
  getBlockedFieldPlaceholder,
  isBlockedFieldMask,
  SECTION_BAR_ICON_BUTTON_SX,
} from './formBlockUi';
import { typography } from '../../../../styles/theme';
import UrbanZoningSection from './UrbanZoningSection';
import PlanimetriaSection from './PlanimetriaSection';
import LotEntornoUrbanoSection from './LotEntornoUrbanoSection';
import LotInfraestructuraSection from './LotInfraestructuraSection';
import LotRiesgosSection from './LotRiesgosSection';
import { formatSurfaceDisplay, resolveLotAreaM2 } from '../../../utils/surfaceDisplay';
import { hasSharedLotUserContentDisplayData } from '../../../utils/lotUserContentFormUtils';
import { PRIVATE_BLOCK_TITLE_DEFAULT } from '../../../utils/spaceMirrorLots';
import {
  LOT_VIEW_QUOTA_EXCEEDED_MESSAGE,
  isLotPreviouslyViewedForQuota,
} from '../../../utils/lotViewQuota';

const parseNumericForDisplay = (val) => {
  if (val === '' || val === null || val === undefined) return NaN;
  if (typeof val === 'number') return Number.isFinite(val) ? val : NaN;

  const s = String(val).trim();
  if (!s) return NaN;

  if (s.includes(',')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  }

  const dotCount = (s.match(/\./g) || []).length;
  if (dotCount <= 1) {
    return parseFloat(s);
  }
  return parseFloat(s.replace(/\./g, ''));
};

const formatNumberAR = (val, { decimals = 2 } = {}) => {
  if (val === '' || val === null || val === undefined) return '';

  const num = parseNumericForDisplay(val);
  if (Number.isNaN(num)) return val;

  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
};

const formatUsdAmount = (val) => formatNumberAR(val, { decimals: 0 });

const formatSentenceCase = (value) => {
  if (!value) return '';
  return String(value)
    .toLowerCase()
    .split(' ')
    .filter((part) => part !== '')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const normalizeText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const normalizeDisplayValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const INFO_CARD_SX = {
  ...LOT_INFO_DETAIL_SURFACE_SX,
  border: 'none',
  borderRadius: 0,
  bgcolor: 'transparent',
};

const INFO_VALUE_SX = (muted = false, noWrap = false) => lotInfoFieldValueSx({ muted, noWrap });

const LOT_INFO_TOOLTIP_SLOT_PROPS = {
  tooltip: { sx: { bgcolor: '#2E2E2E', fontSize: '12px' } },
  arrow: { sx: { color: '#2E2E2E' } },
};

const copyToClipboard = async (value, onCopied) => {
  const text = String(value ?? '').trim();
  if (!text) return;

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  if (onCopied) onCopied();
};

const RowValueContent = ({
  value,
  startAdornment = '',
  endAdornment = '',
  muted = false,
  masked = false,
  noWrap = false,
  onCopy = null,
  copied = false,
}) => {
  const display = normalizeDisplayValue(value);
  const hasDisplay = display !== '';
  const isCopyable = Boolean(onCopy) && hasDisplay && display !== '-';
  const tooltipText = copied ? 'Copiado' : 'Click para copiar';

  const content = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        maxWidth: '100%',
        cursor: isCopyable ? 'pointer' : 'default',
        borderRadius: 1,
        px: isCopyable ? 0.75 : 0,
        py: isCopyable ? 0.35 : 0,
        transition: 'background-color 0.15s ease, color 0.15s ease',
        '&:hover': isCopyable ? { backgroundColor: 'rgba(255,255,255,0.06)' } : undefined,
        '&:active': isCopyable ? { backgroundColor: 'rgba(255,255,255,0.10)' } : undefined,
        userSelect: isCopyable ? 'none' : 'text',
      }}
      role={isCopyable ? 'button' : undefined}
      tabIndex={isCopyable ? 0 : undefined}
      onClick={isCopyable ? onCopy : undefined}
      onKeyDown={isCopyable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCopy();
        }
      } : undefined}
    >
      {startAdornment && hasDisplay && (
        <Typography sx={LOT_INFO_FIELD_ADORNMENT_SX}>
          {startAdornment}
        </Typography>
      )}
      <Typography sx={{
        ...INFO_VALUE_SX(muted, noWrap),
        ...(masked ? { fontFamily: typography.fontFamily.mono, letterSpacing: '0.1em' } : {}),
      }}>
        {hasDisplay ? display : '\u00A0'}
      </Typography>
      {endAdornment && hasDisplay && (
        <Typography sx={LOT_INFO_FIELD_ADORNMENT_SX}>
          {endAdornment}
        </Typography>
      )}
      {copied && (
        <CheckIcon sx={{ ml: 0.25, fontSize: 16, color: '#4ade80', flexShrink: 0 }} />
      )}
    </Box>
  );

  if (!isCopyable) return content;

  return (
    <Tooltip
      title={tooltipText}
      placement="left"
      arrow
      slotProps={LOT_INFO_TOOLTIP_SLOT_PROPS}
    >
      {content}
    </Tooltip>
  );
};

const PrimaryInfoRow = ({
  label,
  value = '',
  blocked = false,
  previewText = '',
  fallbackText = 'Iniciá sesión para ver este dato',
  startAdornment = '',
  endAdornment = '',
  onCopy = null,
  copied = false,
  copyKey = null,
  onMarkCopied = null,
  hideDivider = false,
  labelFlexBasis = '46%',
  noWrapValue = false,
  blockedTooltip = '',
}) => {
  const blockedDisplay = normalizeDisplayValue(previewText) || fallbackText;
  const visibleDisplay = normalizeDisplayValue(value) || '-';
  const isMaskedDisplay = blocked && isBlockedFieldMask(blockedDisplay);
  const canCopy = !blocked && visibleDisplay !== '-' && visibleDisplay !== '';
  const handleCopy = !canCopy
    ? null
    : onCopy ?? (() => copyToClipboard(visibleDisplay, () => onMarkCopied?.(copyKey ?? label)));

  const valueNode = (
    <>
      <Box sx={{ minWidth: 0, textAlign: 'right' }}>
        <RowValueContent
          value={blocked ? blockedDisplay : visibleDisplay}
          startAdornment={blocked ? '' : startAdornment}
          endAdornment={blocked ? '' : endAdornment}
          muted={blocked}
          masked={isMaskedDisplay}
          noWrap={noWrapValue}
          onCopy={handleCopy}
          copied={copied}
        />
      </Box>

      {blocked ? (
        endAdornment ? (
          <Typography sx={LOT_INFO_FIELD_ADORNMENT_SX}>
            {endAdornment}
          </Typography>
        ) : (
          <LockOutlinedIcon sx={{ mt: 0.1, fontSize: 15, color: 'rgba(255,255,255,0.22)', flexShrink: 0 }} />
        )
      ) : null}
    </>
  );

  return (
    <Box sx={lotInfoRowOuterSx(hideDivider)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          ...LOT_INFO_ROW_INNER_SX,
        }}
      >
      <Typography sx={{ ...lotInfoFieldLabelLayoutSx(labelFlexBasis) }}>
        {label}
      </Typography>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        {blocked && blockedTooltip ? (
          <Tooltip
            title={blockedTooltip}
            placement="left"
            arrow
            slotProps={LOT_INFO_TOOLTIP_SLOT_PROPS}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                maxWidth: '100%',
                cursor: 'help',
              }}
            >
              {valueNode}
            </Box>
          </Tooltip>
        ) : valueNode}
      </Box>
      </Box>
    </Box>
  );
};

/** Ícono Editar del header de “Publicación” (registro guardado). */
const PUBLICATION_ICON_BUTTON_SX = {
  width: 28,
  height: 28,
  p: 0,
  border: 'none',
  bgcolor: 'transparent',
  color: 'rgba(255,255,255,0.55)',
  '&:hover': {
    bgcolor: 'transparent',
    color: 'rgba(255,255,255,0.88)',
  },
  '& .MuiSvgIcon-root': { fontSize: 16 },
  '&.Mui-disabled': { opacity: 0.5 },
};

const LotInfoTab = ({
  editedInfo,
  maskSensitive = false,
  lotId,
  canEdit = false,
  listingCoreOnly = false,
  fieldSx = {},
  secondaryTextSx = {},
  userLotLists = [],
  onListCreated,
  sharedLotUserContent = null,
  initialOpenLotContent = false,
  onInitialOpenLotContentConsumed,
  accentColor = null,
  spaceOwnerUserId = null,
  privateBlockTitle = PRIVATE_BLOCK_TITLE_DEFAULT,
  /** R1: "Consultas y leads" del lote (la arma DockPanel). Arriba dentro de "Información privada", solo con registro privado guardado. */
  recordLeads = null,
  recordCampaigns = null,
  /** Comparación de mercado: `({ onEdit, revision }) => node` (la arma DockPanel). */
  renderMarketComparison = null,
}) => {
  const [copiedField, setCopiedField] = useState(null);
  const [lotContentCounts, setLotContentCounts] = useState(null);
  const [isLotContentCountsLoading, setIsLotContentCountsLoading] = useState(false);
  const [isContentFormOpen, setIsContentFormOpen] = useState(false);
  const [contentViewKey, setContentViewKey] = useState(0);
  const [forceExpandPrivateContent, setForceExpandPrivateContent] = useState(false);

  const markCopied = (key) => {
    if (!key) return;
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCopyCadastreId = async () => {
    const value = String(editedInfo?.name || '').trim();
    await copyToClipboard(value, () => markCopied('Numero Catastral'));
  };

  const handleCopyCoords = async () => {
    const lat = editedInfo?.lat;
    const lng = editedInfo?.lng;
    if (lat == null || lng == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return;
    const value = `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
    await copyToClipboard(value, () => markCopied('Coordenadas (lat, lng)'));
  };

  const handleCopyTerrioUuid = async () => {
    const value = String(editedInfo?.id || '').trim();
    await copyToClipboard(value, () => markCopied('Terrio UUID'));
  };

  const effectiveLotId = lotId || editedInfo?.id;
  // Espacio ajeno: el invitado lee (F1) la información privada del owner
  // (`?owner_user_id=`) y, desde F2, la edita con `canEdit` + `spaceOwnerUserId`
  // (el formulario manda el mismo owner y solo el copy comercial).
  const canReadPrivateContent = canEdit || Boolean(spaceOwnerUserId);

  useEffect(() => {
    setIsContentFormOpen(false);
    setForceExpandPrivateContent(false);
    setContentViewKey(0);
  }, [effectiveLotId]);

  // Deep-link desde REGISTROS (item_type === "lot_content"): expandir información privada.
  useEffect(() => {
    if (!initialOpenLotContent || !canReadPrivateContent || !effectiveLotId || maskSensitive) return;
    setIsContentFormOpen(false);
    setForceExpandPrivateContent(true);
    onInitialOpenLotContentConsumed?.();
  }, [
    initialOpenLotContent,
    canReadPrivateContent,
    effectiveLotId,
    maskSensitive,
    onInitialOpenLotContentConsumed,
  ]);

  useEffect(() => {
    if (!effectiveLotId || maskSensitive || !canReadPrivateContent) {
      setLotContentCounts(null);
      setIsLotContentCountsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLotContentCounts(null);
    setIsLotContentCountsLoading(true);

    (async () => {
      try {
        const res = await getLotUserContent(effectiveLotId, { ownerUserId: spaceOwnerUserId });
        if (cancelled) return;
        if (res._httpOk && res.data?.counts) {
          setLotContentCounts(res.data.counts);
        } else {
          setLotContentCounts(null);
        }
      } catch {
        if (!cancelled) setLotContentCounts(null);
      } finally {
        if (!cancelled) setIsLotContentCountsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [effectiveLotId, maskSensitive, canReadPrivateContent, spaceOwnerUserId]);

  const refreshLotContentCounts = async () => {
    if (!effectiveLotId) return;
    try {
      const res = await getLotUserContent(effectiveLotId, { ownerUserId: spaceOwnerUserId });
      if (res._httpOk && res.data?.counts) {
        setLotContentCounts(res.data.counts);
      }
    } catch {
      // ignore
    }
  };

  const handleContentFormClose = () => {
    setIsContentFormOpen(false);
    refreshLotContentCounts();
    setContentViewKey((key) => key + 1);
  };

  const handleOpenContentEdit = () => {
    setIsContentFormOpen(true);
  };

  const handleContentSaved = (data) => {
    if (data?.counts) setLotContentCounts(data.counts);
    else if (listingCoreOnly) setLotContentCounts((prev) => ({ ...(prev || {}), has_content: true }));
    setContentViewKey((key) => key + 1);
  };

  const hasLotContent = Boolean(lotContentCounts?.has_content);
  const hasSharedPrivateContent = hasSharedLotUserContentDisplayData(sharedLotUserContent);
  const showPrivateContentBody = hasLotContent || hasSharedPrivateContent;
  const privateSectionCollapsible = showPrivateContentBody;
  const privateSectionDefaultExpanded = forceExpandPrivateContent
    || (!canEdit && hasSharedPrivateContent);

  if (!editedInfo) return null;

  if (canEdit && isContentFormOpen && effectiveLotId) {
    return (
      <LotUserContentForm
        lotId={effectiveLotId}
        fieldSx={fieldSx}
        secondaryTextSx={secondaryTextSx}
        userLotLists={userLotLists}
        onListCreated={onListCreated}
        onCancel={handleContentFormClose}
        onSaved={handleContentSaved}
        listingCoreOnly={listingCoreOnly}
        initialContent={sharedLotUserContent}
        privateBlockTitle={privateBlockTitle}
        spaceOwnerUserId={spaceOwnerUserId}
      />
    );
  }

  const quota = editedInfo?.quota || null;
  const isQuotaUnlimited = quota?.unlimited === true;
  const isQuotaExceeded = !maskSensitive && !isQuotaUnlimited && quota?.exceeded === true;

  const hasPrivateName =
    editedInfo.name !== undefined &&
    editedInfo.name !== null &&
    String(editedInfo.name).trim() !== '';

  const hasPrivateValuation =
    editedInfo.valuation !== undefined &&
    editedInfo.valuation !== null &&
    String(editedInfo.valuation).trim() !== '';

  const hasPrivateUrbanSurface =
    editedInfo.urbansurface !== undefined &&
    editedInfo.urbansurface !== null &&
    String(editedInfo.urbansurface).trim() !== '';

  const hasPrivateType =
    editedInfo.type !== undefined &&
    editedInfo.type !== null &&
    String(editedInfo.type).trim() !== '';

  const hasPrivateState =
    editedInfo.state !== undefined &&
    editedInfo.state !== null &&
    String(editedInfo.state).trim() !== '';

  const hasViewedLotDetails = hasPrivateName
    || hasPrivateUrbanSurface
    || hasPrivateValuation
    || hasPrivateType
    || hasPrivateState;

  const isLotPreviouslyViewed = isLotPreviouslyViewedForQuota(quota, {
    hasViewedLotDetails,
    isQuotaExceeded,
  });
  const hideDetailForQuota = isQuotaExceeded && !isLotPreviouslyViewed;
  const blockedFieldOptions = { maskSensitive, isQuotaExceeded: hideDetailForQuota };
  const blockedFieldPlaceholder = (fieldKey) => getBlockedFieldPlaceholder(fieldKey, blockedFieldOptions);
  const blockedFieldTooltip = maskSensitive
    ? 'Iniciá sesión para ver este dato'
    : hideDetailForQuota
      ? LOT_VIEW_QUOTA_EXCEEDED_MESSAGE
      : '';
  const sectionBlockedMessage = maskSensitive
    ? 'Iniciá sesión para ver este dato.'
    : hideDetailForQuota
      ? LOT_VIEW_QUOTA_EXCEEDED_MESSAGE
      : 'Este dato no está disponible.';

  const hasCoords =
    !maskSensitive &&
    !hideDetailForQuota &&
    editedInfo.lat != null &&
    editedInfo.lng != null &&
    !Number.isNaN(Number(editedInfo.lat)) &&
    !Number.isNaN(Number(editedInfo.lng));

  const coordsDisplay = hasCoords
    ? `${Number(editedInfo.lat).toFixed(6)}, ${Number(editedInfo.lng).toFixed(6)}`
    : '';
  const provinceNormalized = normalizeText(editedInfo.province);
  const departmentLabel = provinceNormalized === 'buenos aires'
    ? 'Partido'
    : (provinceNormalized === 'caba' || provinceNormalized === 'ciudad autonoma de buenos aires')
      ? 'Comuna'
      : 'Departamento';

  const isCaba =
    provinceNormalized === 'caba' ||
    provinceNormalized === 'ciudad autonoma de buenos aires';

  const lotAreaM2 = resolveLotAreaM2(editedInfo);
  const surfaceDisplay = lotAreaM2 != null ? formatSurfaceDisplay(lotAreaM2) : null;

  const blockedPreview = (preview) => (maskSensitive ? '' : preview);

  const privateInfoAction = canEdit ? (
    <Tooltip
      title={isLotContentCountsLoading
        ? 'Cargando…'
        : hasLotContent
          ? 'Editar información privada del lote'
          : 'Agregar información privada del lote'}
      placement="top"
      arrow
    >
      <span>
        <IconButton
          size="small"
          onClick={handleOpenContentEdit}
          disabled={isLotContentCountsLoading}
          aria-label={isLotContentCountsLoading
            ? 'Cargando información privada del lote'
            : hasLotContent
              ? 'Editar información privada del lote'
              : 'Agregar información privada del lote'}
          sx={hasLotContent ? PUBLICATION_ICON_BUTTON_SX : SECTION_BAR_ICON_BUTTON_SX}
        >
          {isLotContentCountsLoading
            ? <CircularProgress size={12} sx={{ color: 'rgba(255,255,255,0.45)' }} />
            : hasLotContent
              ? <EditIcon />
              : <AddIcon />}
        </IconButton>
      </span>
    </Tooltip>
  ) : null;

  return (
    // pt: 2 = el aire que dejaba el wrapper del banner de cuota (px 2, pt 2) antes de ocultarlo (7eaba04).
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: 2 }}>
      {/* Banner de cuota (usadas / restantes) oculto: ver docs/maps/lot-view-quota.md. */}
      {(canEdit || hasSharedPrivateContent || (spaceOwnerUserId && hasLotContent)) && (
        <LotInfoCollapseSection
          key={`${effectiveLotId}-private-${privateSectionDefaultExpanded ? 'open' : 'base'}-${hasLotContent || hasSharedPrivateContent ? 'content' : 'empty'}`}
          title="Información privada"
          collapsible={privateSectionCollapsible}
          showCollapseToggle={privateSectionCollapsible}
          defaultExpanded={privateSectionDefaultExpanded}
          // Con registro guardado, Editar vive en el header de "Publicación" (la parte editable).
          actions={hasLotContent && canReadPrivateContent && !hasSharedPrivateContent ? null : privateInfoAction}
        >
          {hasSharedPrivateContent ? (
            <Box sx={{ px: 2, py: 1.5 }}>
              <LotUserContentSharedSection
                content={sharedLotUserContent}
                lotContext={editedInfo}
                accentColor={accentColor}
                embedded
              />
            </Box>
          ) : hasLotContent && canReadPrivateContent && effectiveLotId ? (
            <>
            {/* R1: "Consultas y leads" solo dentro de Información privada con registro guardado. */}
            {recordLeads ? <Box sx={{ px: 2, pt: 1.5 }}>{recordLeads}</Box> : null}
            {/* Meta Ads E4: «Campañas» al lado de leads, mismas condiciones. */}
            {recordCampaigns ? <Box sx={{ px: 2, pt: 1.5 }}>{recordCampaigns}</Box> : null}
            <LotUserContentView
              key={`${effectiveLotId}-${spaceOwnerUserId || 'own'}-content-${contentViewKey}`}
              lotId={effectiveLotId}
              userLotLists={userLotLists}
              ownerUserId={spaceOwnerUserId}
              privateBlockTitle={privateBlockTitle}
              publicationAction={privateInfoAction}
              embedded
              // «Precio vs. mercado» debajo de «Datos comerciales» (precio del lote).
              afterCommercial={renderMarketComparison ? renderMarketComparison({
                onEdit: canEdit ? handleOpenContentEdit : undefined,
                revision: contentViewKey,
              }) : null}
            />
            </>
          ) : null}
        </LotInfoCollapseSection>
      )}

      <Box sx={{ mt: 0, pb: 1 }}>
      <LotInfoCollapseSection key={`${effectiveLotId}-principal`} title="Información principal">
        <Box sx={INFO_CARD_SX}>
          <PrimaryInfoRow
            label="Terrio UUID"
            value={editedInfo.id ?? '-'}
            blocked={maskSensitive || hideDetailForQuota}
            blockedTooltip={blockedFieldTooltip}
            previewText=""
            fallbackText={blockedFieldPlaceholder('Terrio UUID')}
            onCopy={!maskSensitive && !hideDetailForQuota && editedInfo.id ? handleCopyTerrioUuid : null}
            copied={copiedField === 'Terrio UUID'}
            copyKey="Terrio UUID"
            onMarkCopied={markCopied}
            labelFlexBasis="34%"
            noWrapValue
          />

          <PrimaryInfoRow
            label="Número Catastral"
            value={hasPrivateName ? editedInfo.name : '-'}
            blocked={maskSensitive || (!hasPrivateName && isQuotaExceeded)}
            blockedTooltip={blockedFieldTooltip}
            previewText={blockedPreview(hasPrivateName ? String(editedInfo.name).trim() : '')}
            fallbackText={blockedFieldPlaceholder('Número Catastral')}
            onCopy={!maskSensitive && hasPrivateName ? handleCopyCadastreId : null}
            copied={copiedField === 'Numero Catastral'}
            copyKey="Numero Catastral"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Superficie"
            value={surfaceDisplay?.value ?? '-'}
            blocked={maskSensitive || (!hasPrivateUrbanSurface && isQuotaExceeded)}
            blockedTooltip={blockedFieldTooltip}
            previewText={blockedPreview(surfaceDisplay?.value ?? '')}
            fallbackText={blockedFieldPlaceholder('Superficie')}
            endAdornment={surfaceDisplay?.unit ?? 'm²'}
            copied={copiedField === 'Superficie'}
            copyKey="Superficie"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Tipo"
            value={hasPrivateType ? editedInfo.type : '-'}
            blocked={maskSensitive || (!hasPrivateType && isQuotaExceeded)}
            blockedTooltip={blockedFieldTooltip}
            previewText={blockedPreview(editedInfo.type || '')}
            fallbackText={blockedFieldPlaceholder('Tipo')}
            copied={copiedField === 'Tipo'}
            copyKey="Tipo"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Estado"
            value={hasPrivateState ? editedInfo.state : '-'}
            blocked={maskSensitive || (!hasPrivateState && isQuotaExceeded)}
            blockedTooltip={blockedFieldTooltip}
            previewText={blockedPreview(editedInfo.state || '')}
            fallbackText={blockedFieldPlaceholder('Estado')}
            copied={copiedField === 'Estado'}
            copyKey="Estado"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Provincia"
            value={editedInfo.province || '-'}
            copied={copiedField === 'Provincia'}
            copyKey="Provincia"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label={departmentLabel}
            value={editedInfo.department || '-'}
            copied={copiedField === departmentLabel}
            copyKey={departmentLabel}
            onMarkCopied={markCopied}
          />

          {!isCaba && (
            <PrimaryInfoRow
              label="Municipio"
              value={editedInfo.municipality || '-'}
              copied={copiedField === 'Municipio'}
              copyKey="Municipio"
              onMarkCopied={markCopied}
            />
          )}

          <PrimaryInfoRow
            label="Barrio"
            value={editedInfo.neighborhood || '-'}
            copied={copiedField === 'Barrio'}
            copyKey="Barrio"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Calle"
            value={
              editedInfo.street || editedInfo.street_name
                ? formatSentenceCase(editedInfo.street || editedInfo.street_name)
                : '-'
            }
            copied={copiedField === 'Calle'}
            copyKey="Calle"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Coordenadas (lat, lng)"
            value={coordsDisplay}
            blocked={!hasCoords}
            blockedTooltip={blockedFieldTooltip}
            previewText=""
            fallbackText={blockedFieldPlaceholder('Coordenadas (lat, lng)')}
            onCopy={hasCoords ? handleCopyCoords : null}
            copied={copiedField === 'Coordenadas (lat, lng)'}
            copyKey="Coordenadas (lat, lng)"
            onMarkCopied={markCopied}
          />

          <PrimaryInfoRow
            label="Valuación"
            value={hasPrivateValuation ? formatUsdAmount(editedInfo.valuation) : '-'}
            blocked={maskSensitive || (!hasPrivateValuation && isQuotaExceeded)}
            blockedTooltip={blockedFieldTooltip}
            previewText={blockedPreview(hasPrivateValuation ? formatUsdAmount(editedInfo.valuation) : '')}
            fallbackText={blockedFieldPlaceholder('Valuación')}
            startAdornment={maskSensitive ? '' : 'USD'}
            copied={copiedField === 'Valuacion'}
            copyKey="Valuacion"
            onMarkCopied={markCopied}
            hideDivider
          />
        </Box>
      </LotInfoCollapseSection>

      <LotInfoCollapseSection key={`${effectiveLotId}-planimetria`} title="Planimetría">
        <PlanimetriaSection
          hideTitle
          boundary={editedInfo.boundary}
          declaredSurfaceM2={lotAreaM2}
          frontAzimuth={editedInfo.front_azimuth}
          lotShape={editedInfo.lot_shape}
          blocked={maskSensitive || hideDetailForQuota}
          blockedMessage={
            maskSensitive
              ? 'Iniciá sesión para ver la planimetría.'
              : sectionBlockedMessage
          }
        />
      </LotInfoCollapseSection>

      <LotInfoCollapseSection key={`${effectiveLotId}-normativa`} title="Normativa urbana">
        <UrbanZoningSection
          hideTitle
          builtFootprintM2={editedInfo.built_footprint_m2}
          maxFootprintM2={editedInfo.max_footprint_m2}
          maxBuildableM2={editedInfo.max_buildable_m2}
          zoningPerfil={editedInfo.zoning_perfil}
          blocked={maskSensitive || hideDetailForQuota}
          blockedMessage={
            maskSensitive
              ? 'Iniciá sesión para ver la normativa urbana del lote.'
              : sectionBlockedMessage
          }
        />
      </LotInfoCollapseSection>

      <LotInfoCollapseSection key={`${effectiveLotId}-entorno`} title="Entorno urbano">
        <LotEntornoUrbanoSection
          hideTitle
          frontHighwayType={editedInfo.front_highway_type}
          streetPosition={editedInfo.street_position}
          neighborhoodType={editedInfo.neighborhood_type}
          urbanSurface={lotAreaM2}
          populationDensity={editedInfo.population_density}
          blocked={maskSensitive || hideDetailForQuota}
          blockedMessage={
            maskSensitive
              ? 'Iniciá sesión para ver el entorno urbano del lote.'
              : sectionBlockedMessage
          }
        />
      </LotInfoCollapseSection>

      <LotInfoCollapseSection key={`${effectiveLotId}-infraestructura`} title="Infraestructura">
        <LotInfraestructuraSection
          hideTitle
          waterNetworkDeficitPct={editedInfo.water_network_deficit_pct}
          sewerDeficitPct={editedInfo.sewer_deficit_pct}
          cookingFuelDeficitPct={editedInfo.cooking_fuel_deficit_pct}
          blocked={maskSensitive || hideDetailForQuota}
          blockedMessage={
            maskSensitive
              ? 'Iniciá sesión para ver la infraestructura del lote.'
              : sectionBlockedMessage
          }
        />
      </LotInfoCollapseSection>

      <LotInfoCollapseSection key={`${effectiveLotId}-riesgos`} title="Riesgos / Contexto">
        <LotRiesgosSection
          hideTitle
          floodRiskPct={editedInfo.flood_risk_pct}
          chronicPovertyPct={editedInfo.chronic_poverty_pct}
          blocked={maskSensitive || hideDetailForQuota}
          blockedMessage={
            maskSensitive
              ? 'Iniciá sesión para ver los riesgos del entorno del lote.'
              : sectionBlockedMessage
          }
        />
      </LotInfoCollapseSection>
      </Box>
    </Box>
  );
};

export default LotInfoTab;